/**
 * Dev-widget channel loader (ShowPilot QA only) — the widget-bundle
 * counterpart of the `?sp_env` backend toggle in `config.ts`.
 *
 * Lets ShowPilot staff load a BRANCH build of the widget on a live page
 * without touching the published `/widget/v1/` bundle. Branch builds are
 * published to the public R2 bucket by `scripts/publish-dev-widget.mjs`
 * under `system/widget-dev/<channel>/app.js`; this shim (which ships in
 * /v1) swaps them in when the override is active:
 *
 *   ?sp_widget=<channel> → persist channel + load its bundle instead
 *   ?sp_widget=off       → clear the override (back to the /v1 bundle)
 *
 * Combine with `?sp_env=dev` to point the branch widget at the dev
 * backend: `showpilot.com/?sp_env=dev&sp_widget=next`.
 *
 * Security posture: the override accepts a CHANNEL NAME ONLY (strict
 * charset), never a URL — the bundle always loads from ShowPilot's own
 * R2 public host, so a crafted link can't make a customer page execute
 * third-party script. Same "gate is knowing the param" model as sp_env.
 */

// Public R2 host (already public in every served asset URL). Channel
// bundles are uploaded with no-store cache-control; the boot also
// cache-busts so staff always test the latest publish.
const DEV_WIDGET_BASE =
  'https://pub-b83d2cb8bc8342bf98eddd853dc4acad.r2.dev/system/widget-dev';

const CHANNEL_LS_KEY = 'sp_widget_channel';
const CHANNEL_AT_KEY = 'sp_widget_channel_at';
const CHANNEL_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/**
 * Staff pins self-expire. A forgotten localStorage pin left this
 * browser+origin serving a stale BRANCH bundle for days (live catch
 * 2026-07-13: agent navigation crossed www/apex origins with different
 * pin states → the widget package alternated page-to-page and the rail
 * chrome flickered CTAs). 24h keeps a QA session sticky without
 * haunting next week's testing.
 */
const CHANNEL_TTL_MS = 24 * 60 * 60 * 1000;

// Recursion guard: the channel bundle contains this same shim; the
// global marks "an override bundle is (being) loaded on this page" so
// the channel bundle boots normally instead of re-injecting itself.
declare global {
  interface Window {
    __spWidgetChannelLoaded?: string;
  }
}

function lsGet(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}
function lsSet(key: string, val: string): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, val);
  } catch {
    /* storage unavailable — ignore */
  }
}
function lsDel(key: string): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable — ignore */
  }
}

/** Validate + normalize a channel value; null when it isn't one. */
export function parseWidgetChannel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  return CHANNEL_RE.test(v) ? v : null;
}

/**
 * Process `?sp_widget` once at boot and persist the result — same
 * one-shot-activator semantics as `applyEnvOverrideFromUrl` (param is
 * stripped from the address bar; navigation runs off localStorage).
 */
export function applyWidgetChannelFromUrl(): void {
  if (typeof window === 'undefined') return;
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(window.location.search).get('sp_widget');
  } catch {
    /* malformed search — ignore */
  }
  if (raw === null) return;
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('sp_widget');
    window.history.replaceState(null, '', u.toString());
  } catch {
    /* history unavailable — ignore */
  }
  if (raw === 'off' || raw === 'clear') {
    lsDel(CHANNEL_LS_KEY);
    lsDel(CHANNEL_AT_KEY);
    return;
  }
  const channel = parseWidgetChannel(raw);
  if (channel) {
    lsSet(CHANNEL_LS_KEY, channel);
    lsSet(CHANNEL_AT_KEY, String(Date.now()));
  }
}

/** The persisted channel override, if any (re-validated + TTL'd on read). */
export function getWidgetChannel(): string | null {
  const channel = parseWidgetChannel(lsGet(CHANNEL_LS_KEY));
  if (!channel) return null;
  const at = Number(lsGet(CHANNEL_AT_KEY) ?? 0);
  if (!at || Date.now() - at > CHANNEL_TTL_MS) {
    // Expired (or legacy pin with no timestamp) — self-heal.
    lsDel(CHANNEL_LS_KEY);
    lsDel(CHANNEL_AT_KEY);
    console.warn(`[showpilot] dev-widget channel pin "${channel}" expired — back to the /v1 bundle`);
    return null;
  }
  console.warn(
    `[showpilot] DEV-WIDGET OVERRIDE ACTIVE: channel "${channel}" (staff pin, this origin). ?sp_widget=off to clear.`,
  );
  return channel;
}

/** The bundle URL for a channel (cache-busted — dev builds must never stick). */
export function widgetChannelUrl(channel: string): string {
  return `${DEV_WIDGET_BASE}/${channel}/app.js?v=${Date.now()}`;
}

/**
 * If a channel override is active and we're NOT already the override
 * bundle, inject the channel bundle and tell the caller to skip its own
 * boot. Called at the very top of `init()`.
 */
export function maybeLoadWidgetChannel(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (window.__spWidgetChannelLoaded) return false; // we ARE the channel bundle
  const channel = getWidgetChannel();
  if (!channel) return false;
  window.__spWidgetChannelLoaded = channel;
  const s = document.createElement('script');
  s.async = true;
  s.src = widgetChannelUrl(channel);
  s.onerror = () => {
    // Channel bundle missing/unreachable — fall back to booting THIS
    // bundle so the page never ends up with no widget at all.
    console.warn(`[showpilot] dev widget channel '${channel}' failed to load — using /v1`);
    delete window.__spWidgetChannelLoaded;
    lsDel(CHANNEL_LS_KEY);
    window.__spWidgetFallbackBoot?.();
  };
  document.head.appendChild(s);
  console.info(`[showpilot] loading dev widget channel '${channel}' (sp_widget=off to exit)`);
  return true;
}

declare global {
  interface Window {
    /** Set by index.tsx so the shim's onerror can boot the /v1 bundle late. */
    __spWidgetFallbackBoot?: () => void;
  }
}
